import { apiClient } from "@/lib/api";
import { withReadableBlobErrorBody } from "@/lib/blobErrorBody";
import type {
  AdminSearchItem,
  AdminSearchResponse,
  AdminSearchType,
  Broadcast,
  BroadcastAudience,
  BroadcastCountry,
  BroadcastListItem,
  BroadcastListResponse,
  BroadcastPlanTier,
  CreateBroadcastData,
  MaintenanceBanner,
  UpdateMaintenanceBannerData,
  ActivityLogCategory,
  ActivityLogItem,
  ActivityLogPage,
  ActivityLogType,
  AdminAlert,
  AdminAlertList,
  AdminAlertMarkResponse,
  AdminBillingStats,
  AdminBillingStopEvent,
  AdminBillingStopEventListResponse,
  AdminBillingStopEventType,
  AdminDashboard,
  AdminDashboardPeriod,
  BillingRevenueSummary,
  AdminDealer,
  AdminDealerDetail,
  AdminDealerListResponse,
  AdminGlobalUserPage,
  AdminTenant,
  AdminTenantDetail,
  AdminTenantPage,
  AdminTenantUser,
  AdminTenantUserPage,
  AssignTenantsResponse,
  BillingMonth,
  BillingRecord,
  BillingRecordBreakdown,
  BillingRecordListResponse,
  BulkGenerateBillingResponse,
  BulkTenantUpdateResponse,
  ChurnRiskResponse,
  ChurnRiskRow,
  CreateAdminDealerData,
  CreateCreditNoteData,
  CreateTenantUserData,
  CreditNote,
  CreditNoteListResponse,
  RecordPaymentData,
  MrrSeriesPoint,
  MrrSeriesResponse,
  RetentionCohort,
  RetentionResponse,
  GenerateBillingResponse,
  ImpersonateDealerResponse,
  ImpersonateTenantResponse,
  PlatformSettings,
  TenantBilling,
  TenantBillingSettings,
  TenantBillingSettingsFormData,
  TenantPlan,
  TenantPlanningSettings,
  TenantSettings,
  UpdateTenantPlanData,
  UnassignTenantsResponse,
  UnitEconomicsResponse,
  UpdateAdminDealerData,
  UpdateAdminUserData,
} from "@/types/admin";
import type { DetailedInvoiceJob } from "@/types/billing";
import { invoiceFilename } from "@/api/billing";

/** Query parameters accepted by the server-paginated tenant list (TASK-504). */
export interface ListAdminTenantsParams {
  search?: string;
  country?: "EU" | "BY";
  is_active?: boolean;
  created_from?: string;
  created_to?: string;
  ordering?: string;
  page?: number;
  page_size?: number;
}

/**
 * Fetch tenants for the superadmin panel as a server-paginated, filtered
 * envelope ({count, next, previous, results}). All params are optional and
 * AND-combined on the backend.
 */
export async function listAdminTenants(
  params: ListAdminTenantsParams = {},
): Promise<AdminTenantPage> {
  const response = await apiClient.get<AdminTenantPage>("/v1/admin/tenants/", {
    params,
  });
  return response.data;
}

/**
 * Fetch a single tenant by id with the same stats + suspension metadata as a
 * list row. Backed by `GET /api/v1/admin/tenants/:id/` (BUG-159) so the detail
 * page loads directly instead of scanning the paginated list.
 */
export async function getAdminTenant(id: number): Promise<AdminTenant> {
  const response = await apiClient.get<AdminTenant>(`/v1/admin/tenants/${id}/`);
  return response.data;
}

/**
 * Fetch a single tenant by id together with its computed summary metrics
 * (orders/routes/vehicles totals, active routes today, last activity).
 * Backed by `GET /api/v1/admin/tenants/:id/` (TASK-3607) — used by
 * AdminTenantDetailPage to render the KPI cards (TASK-3608).
 */
export async function getTenantDetail(id: number): Promise<AdminTenantDetail> {
  const response = await apiClient.get<AdminTenantDetail>(
    `/v1/admin/tenants/${id}/`,
  );
  return response.data;
}

/**
 * Update a tenant's name and/or active status.
 * When deactivating (is_active=false) a `suspension_reason` is required by the
 * backend (400 with a field error otherwise).
 */
export async function updateAdminTenant(
  id: number,
  data: { name?: string; is_active?: boolean; suspension_reason?: string },
): Promise<AdminTenant> {
  const response = await apiClient.patch<AdminTenant>(
    `/v1/admin/tenants/${id}/`,
    data,
  );
  return response.data;
}

/**
 * Bulk activate/deactivate tenants (TASK-506). When deactivating, a
 * `suspension_reason` is required and applied to all given tenants.
 */
export async function bulkUpdateTenants(data: {
  ids: number[];
  is_active: boolean;
  suspension_reason?: string;
}): Promise<BulkTenantUpdateResponse> {
  const response = await apiClient.post<BulkTenantUpdateResponse>(
    "/v1/admin/tenants/bulk-update/",
    data,
  );
  return response.data;
}

/**
 * Export the selected tenants as a CSV (TASK-3615/TASK-3616). Backed by
 * `POST /api/v1/admin/tenants/export-selected/` with an `ids` body (max 100,
 * deduplicated server-side; empty ids → 400). Returns the CSV blob together
 * with the filename suggested by the `Content-Disposition` header (falls back
 * to `tenants-selected.csv` when absent).
 */
export async function exportSelectedTenants(
  ids: number[],
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.post<Blob>(
    "/v1/admin/tenants/export-selected/",
    { ids },
    { responseType: "blob" },
  );
  const disposition = response.headers["content-disposition"];
  const filename =
    parseContentDispositionFilename(
      typeof disposition === "string" ? disposition : undefined,
    ) ?? "tenants-selected.csv";
  return { blob: response.data, filename };
}

/**
 * Impersonate a tenant as company_admin.
 * Returns short-lived access + refresh tokens scoped to the target tenant.
 */
export async function impersonateTenant(
  tenantId: number,
): Promise<ImpersonateTenantResponse> {
  const response = await apiClient.post<ImpersonateTenantResponse>(
    `/v1/admin/impersonate/${tenantId}/`,
  );
  return response.data;
}

/**
 * Impersonate a dealer as that dealer (Sprint 139, TASK-3906). Superadmin only
 * (`POST /admin/impersonate/dealer/<userId>/`). Returns short-lived access +
 * refresh tokens carrying `role="dealer"` + `dealer_id` (no `tenant_id`), so
 * the caller lands in the /dealer shell scoped to that dealer's own tenants.
 * `userId` is the target dealer USER id (Decision D4).
 */
export async function impersonateDealer(
  userId: number,
): Promise<ImpersonateDealerResponse> {
  const response = await apiClient.post<ImpersonateDealerResponse>(
    `/v1/admin/impersonate/dealer/${userId}/`,
  );
  return response.data;
}

/**
 * End the caller's active impersonation session (TASK-507). Idempotent —
 * safe to call when there is no open session. Best-effort: callers should not
 * block the impersonation-exit flow on this request.
 */
export async function endImpersonation(): Promise<{ detail?: string }> {
  const response = await apiClient.post<{ detail?: string }>(
    "/v1/admin/impersonate/end/",
  );
  return response.data;
}

/**
 * Create a new tenant via the dealer-aware admin endpoint (Sprint 105).
 * The client supplies only `name` + `country`; the backend auto-generates a
 * unique slug from the name. When called by a dealer the new tenant is linked
 * to that dealer server-side (no client `dealer` field). Returns the annotated
 * tenant row.
 */
export async function createAdminTenant(data: {
  name: string;
  country: string;
  /**
   * Optional dealer id to assign the tenant to (superadmin only). Ignored by
   * the backend for a dealer caller (their tenants always belong to them).
   */
  dealer?: number | null;
}): Promise<AdminTenant> {
  const response = await apiClient.post<AdminTenant>(
    "/v1/admin/tenants/",
    data,
  );
  return response.data;
}

/** Query parameters accepted by the superadmin dealer list (TASK-3644). */
export interface ListAdminDealersParams {
  /** Searches dealer email / first name / last name. */
  search?: string;
  page?: number;
}

/**
 * List dealer accounts (superadmin-only, TASK-3644). Returns the paginated
 * envelope ({count, next, previous, results}); each dealer carries its
 * `tenant_count`. Backed by `GET /api/v1/admin/dealers/`.
 */
export async function listAdminDealers(
  params: ListAdminDealersParams = {},
): Promise<AdminDealerListResponse> {
  const response = await apiClient.get<AdminDealerListResponse>(
    "/v1/admin/dealers/",
    { params },
  );
  return response.data;
}

/**
 * Create a dealer account (superadmin-only, TASK-3644). The backend forces
 * `role=dealer`, `tenant=null`, `is_staff=false`. A duplicate email yields a
 * 400 field error on `email`. Backed by `POST /api/v1/admin/dealers/`.
 */
export async function createAdminDealer(
  data: CreateAdminDealerData,
): Promise<AdminDealer> {
  const response = await apiClient.post<AdminDealer>(
    "/v1/admin/dealers/",
    data,
  );
  return response.data;
}

/**
 * Fetch a single dealer by id with its `tenant_count` (superadmin-only).
 * Returns 404 when the target user is not a dealer. Backed by
 * `GET /api/v1/admin/dealers/:id/`.
 */
export async function getAdminDealer(id: number): Promise<AdminDealer> {
  const response = await apiClient.get<AdminDealer>(`/v1/admin/dealers/${id}/`);
  return response.data;
}

/**
 * Fetch a dealer's detail payload (superadmin-only, TASK-3671): the dealer
 * record plus the dealer's `assigned_tenants` (each with current-month stop
 * count, revenue, cost and margin) and a `revenue_summary` roll-up for the
 * current billing month. Returns 404 when the target user is not a dealer.
 * Backed by `GET /api/v1/admin/dealers/:id/`.
 *
 * Kept separate from the slim {@link getAdminDealer} so the list/edit flows are
 * not forced to carry (and re-validate) the heavier detail aggregates.
 */
export async function getAdminDealerDetail(
  id: number,
): Promise<AdminDealerDetail> {
  const response = await apiClient.get<AdminDealerDetail>(
    `/v1/admin/dealers/${id}/`,
  );
  return response.data;
}

/**
 * Update a dealer's first/last name and active status (superadmin-only).
 * Deactivating (`is_active=false`) blocks login but keeps the dealer's tenant
 * assignments intact. Backed by `PATCH /api/v1/admin/dealers/:id/`.
 */
export async function updateAdminDealer(
  id: number,
  data: UpdateAdminDealerData,
): Promise<AdminDealer> {
  const response = await apiClient.patch<AdminDealer>(
    `/v1/admin/dealers/${id}/`,
    data,
  );
  return response.data;
}

/**
 * Delete a dealer account (superadmin-only, TASK-3697). The backend deletes the
 * dealer only when it owns zero tenants and returns 204 No Content; otherwise it
 * responds 409 with `{"detail": "..."}` naming the blocking constraint (the
 * caller surfaces that message via toast and keeps the row). Backed by
 * `DELETE /api/v1/admin/dealers/:id/`.
 */
export async function deleteDealer(id: number): Promise<void> {
  await apiClient.delete(`/v1/admin/dealers/${id}/`);
}

/**
 * Assign (or reassign) the given tenants to a dealer (superadmin-only). Sets
 * `Tenant.dealer = dealer` for each id; reassigning from another dealer is
 * allowed. Backed by `POST /api/v1/admin/dealers/:id/assign-tenants/`.
 */
export async function assignTenantsToDealer(
  id: number,
  tenantIds: number[],
): Promise<AssignTenantsResponse> {
  const response = await apiClient.post<AssignTenantsResponse>(
    `/v1/admin/dealers/${id}/assign-tenants/`,
    { tenant_ids: tenantIds },
  );
  return response.data;
}

/**
 * Unassign the given tenants from a dealer (superadmin-only). Sets
 * `Tenant.dealer = null` for each id. Backed by
 * `POST /api/v1/admin/dealers/:id/unassign-tenants/`.
 */
export async function unassignTenantsFromDealer(
  id: number,
  tenantIds: number[],
): Promise<UnassignTenantsResponse> {
  const response = await apiClient.post<UnassignTenantsResponse>(
    `/v1/admin/dealers/${id}/unassign-tenants/`,
    { tenant_ids: tenantIds },
  );
  return response.data;
}

/**
 * Optional custom date-range bounds accepted by the range-aware admin analytics
 * endpoints (TASK-4310). Both are ISO `YYYY-MM-DD`. When supplied they replace
 * the `period` preset (mutually exclusive server-side); an invalid pair
 * (from > to, only one set, unparseable) yields a 400.
 */
export interface AdminAnalyticsRangeParams {
  date_from?: string;
  date_to?: string;
}

/**
 * Fetch superadmin platform dashboard metrics for the given trend window
 * (TASK-3609/TASK-3610). `period` defaults to `30d` on the backend; the
 * response echoes the resolved window plus current/previous totals,
 * `*_change_pct` deltas, and a zero-filled `daily_series`.
 *
 * Range-aware (TASK-4310/4313): pass a custom `date_from`/`date_to` INSTEAD of
 * `period` to scope the metrics to an explicit range — the response then echoes
 * `period: "custom"` plus the dates. `period` and the date range are mutually
 * exclusive; when both are omitted the backend applies the default preset.
 */
export async function getAdminDashboard(
  period?: AdminDashboardPeriod,
  range: AdminAnalyticsRangeParams = {},
): Promise<AdminDashboard> {
  const params = buildRangeParams(period, range);
  const response = await apiClient.get<AdminDashboard>("/v1/admin/dashboard/", {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return response.data;
}

/**
 * Build the mutually-exclusive `{period}` OR `{date_from,date_to}` query object
 * for a range-aware endpoint. A custom range (both ISO bounds set) wins and
 * omits `period`; otherwise only `period` (when given) is sent. Shared by the
 * dashboard / revenue-summary / MRR / retention helpers (TASK-4313).
 */
function buildRangeParams(
  period: AdminDashboardPeriod | undefined,
  range: AdminAnalyticsRangeParams,
): Record<string, string> {
  if (range.date_from && range.date_to) {
    return { date_from: range.date_from, date_to: range.date_to };
  }
  return period ? { period } : {};
}

/** Optional params for the revenue-summary endpoint (TASK-3673). */
export interface GetBillingRevenueSummaryParams {
  /**
   * When true, the response adds a per-tenant revenue/cost/margin breakdown
   * (`per_tenant` + `per_tenant_totals`) for the current billing month, scoped
   * via `tenants_visible_to`.
   */
  breakdown?: boolean;
  /**
   * Trend window (`7d|30d|90d`); when set the response adds a zero-filled
   * `margin_series`. Defaults to `30d` on the backend.
   */
  period?: AdminDashboardPeriod;
  /**
   * Custom range bounds (ISO `YYYY-MM-DD`); when both are set they replace
   * `period` (mutually exclusive server-side) and scope the `margin_series`
   * to the explicit range (TASK-4313).
   */
  date_from?: string;
  date_to?: string;
}

/**
 * Fetch the revenue roll-up (billed / paid / overdue + current-month MRR) across
 * the caller's visible tenants (TASK-3653 / TASK-3662). Backed by
 * `GET /api/v1/admin/billing/revenue-summary/`. Dealer-scoped server-side via
 * `tenants_visible_to(user)`: a dealer gets their own portfolio, a superadmin
 * gets the platform-wide roll-up.
 *
 * The optional `breakdown` / `period` params (TASK-3673) additively extend the
 * response with `per_tenant` + `per_tenant_totals` and a `margin_series`
 * respectively; existing zero-arg callers (the dashboard roll-up) are
 * unaffected — the base response shape is unchanged when no params are passed.
 */
export async function getBillingRevenueSummary(
  params: GetBillingRevenueSummaryParams = {},
): Promise<BillingRevenueSummary> {
  const query: {
    breakdown?: boolean;
    period?: AdminDashboardPeriod;
    date_from?: string;
    date_to?: string;
  } = {};
  if (params.breakdown) {
    query.breakdown = true;
  }
  // A custom range (both bounds) replaces `period` (mutually exclusive).
  if (params.date_from && params.date_to) {
    query.date_from = params.date_from;
    query.date_to = params.date_to;
  } else if (params.period) {
    query.period = params.period;
  }
  const response = await apiClient.get<BillingRevenueSummary>(
    "/v1/admin/billing/revenue-summary/",
    Object.keys(query).length > 0 ? { params: query } : undefined,
  );
  return response.data;
}

/**
 * Download the SUMMARY invoice PDF for the superadmin / dealer billing page.
 * Backed by `GET /api/v1/admin/billing/records/{id}/pdf/`: the record is scoped
 * via `tenants_visible_to`, so a dealer can only download invoices for tenants
 * in their own portfolio.
 *
 * Returns PDF BYTES, not a URL (TASK-4813 changed the contract — the document is
 * re-rendered in the caller's UI language on every download, which a single
 * stored S3 file cannot satisfy). Mirrors the tenant-side
 * `downloadBillingRecord`; the two must stay the same shape.
 */
export async function downloadAdminBillingRecord(
  id: number,
  lang: string,
): Promise<{ blob: Blob; filename: string }> {
  // Same blob-error parsing as the tenant download (BUG-704): a refusal's JSON
  // body is read so the toast states the server's reason.
  const response = await apiClient
    .get<Blob>(`/v1/admin/billing/records/${id}/pdf/`, {
      params: { lang },
      responseType: "blob",
    })
    .catch(async (err: unknown) => {
      throw await withReadableBlobErrorBody(err);
    });
  return {
    blob: response.data,
    filename: invoiceFilename(response.headers, id),
  };
}

/**
 * Ask for the DETAILED invoice PDF (admin/dealer surface) and report progress.
 * The admin counterpart of `requestDetailedBillingRecord` — same background
 * render, same job payload, scoped via `tenants_visible_to`.
 */
export async function requestDetailedAdminBillingRecord(
  id: number,
  lang: string,
): Promise<DetailedInvoiceJob> {
  const response = await apiClient.post<DetailedInvoiceJob>(
    `/v1/admin/billing/records/${id}/detailed/`,
    null,
    { params: { lang } },
  );
  return response.data;
}

/**
 * Fetch the superadmin system alerts feed (overdue invoices, suspended tenants,
 * recent optimization failures) annotated with this admin's read/dismiss state.
 * Backed by `GET /api/v1/admin/alerts/` (TASK-3619). Dismissed alerts are
 * excluded by default; pass `includeDismissed=true` (the inbox view, TASK-3676)
 * to receive the full set including dismissed alerts.
 */
export async function getAdminAlerts(
  includeDismissed = false,
): Promise<AdminAlertList> {
  const response = await apiClient.get<AdminAlertList>(
    "/v1/admin/alerts/",
    includeDismissed ? { params: { include_dismissed: true } } : undefined,
  );
  return response.data;
}

/**
 * Mark alerts as read for the calling admin (idempotent). Pass explicit `keys`
 * and/or `all: true` to mark every currently-visible alert read server-side
 * (TASK-3675); after `all`, `unread_count` is 0. Backed by
 * `POST /api/v1/admin/alerts/read/` (TASK-3619).
 */
export async function markAlertsRead(payload: {
  keys?: string[];
  all?: boolean;
}): Promise<AdminAlertMarkResponse> {
  const response = await apiClient.post<AdminAlertMarkResponse>(
    "/v1/admin/alerts/read/",
    payload,
  );
  return response.data;
}

/**
 * Dismiss alerts for the calling admin (idempotent). Pass explicit `keys`
 * and/or `all: true` to dismiss every currently-visible alert (TASK-3675).
 * Dismissed alerts are hidden from the default feed. Backed by
 * `POST /api/v1/admin/alerts/dismiss/` (TASK-3619).
 */
export async function dismissAlerts(payload: {
  keys?: string[];
  all?: boolean;
}): Promise<AdminAlertMarkResponse> {
  const response = await apiClient.post<AdminAlertMarkResponse>(
    "/v1/admin/alerts/dismiss/",
    payload,
  );
  return response.data;
}

/**
 * Un-dismiss alerts for the calling admin (idempotent). Pass explicit `keys`
 * (and/or `all: true`) to clear the per-admin dismissed state so the alert
 * returns to the default (non-`include_dismissed`) feed. Un-dismissing a
 * non-dismissed (or unknown) key is a no-op. Read state is preserved. Backed by
 * `POST /api/v1/admin/alerts/undismiss/` (TASK-3706).
 */
export async function unDismissAlerts(
  keys: string[],
): Promise<AdminAlertMarkResponse> {
  const response = await apiClient.post<AdminAlertMarkResponse>(
    "/v1/admin/alerts/undismiss/",
    { keys },
  );
  return response.data;
}

/** Query parameters accepted by the server-paginated tenant-users list. */
export interface ListTenantUsersParams {
  search?: string;
  role?: "company_admin" | "dispatcher" | "driver";
  is_active?: boolean;
  page?: number;
}

/**
 * List users belonging to a specific tenant as a server-paginated, filtered
 * envelope ({count, next, previous, results}) (TASK-504).
 */
export async function listTenantUsers(
  tenantId: number,
  params: ListTenantUsersParams = {},
): Promise<AdminTenantUserPage> {
  const response = await apiClient.get<AdminTenantUserPage>(
    `/v1/admin/tenants/${tenantId}/users/`,
    { params },
  );
  return response.data;
}

/**
 * Create a new user in a specific tenant.
 */
export async function createTenantUser(
  tenantId: number,
  data: CreateTenantUserData,
): Promise<AdminTenantUser> {
  const response = await apiClient.post<AdminTenantUser>(
    `/v1/admin/tenants/${tenantId}/users/`,
    data,
  );
  return response.data;
}

/**
 * Partially update a user's profile fields (first_name, last_name, role, is_active).
 */
export async function updateAdminUser(
  userId: number,
  data: UpdateAdminUserData,
): Promise<AdminTenantUser> {
  const response = await apiClient.patch<AdminTenantUser>(
    `/v1/admin/users/${userId}/`,
    data,
  );
  return response.data;
}

/** Query parameters accepted by the global cross-tenant user search (TASK-3685, TASK-3892). */
export interface SearchAdminUsersParams {
  /** Matches user email / first name / last name (icontains). */
  search?: string;
  /** Filter by role: company_admin | dispatcher | driver. */
  role?: string;
  /** Filter by status: active | inactive. */
  status?: string;
  /** Filter to a single tenant. */
  tenant_id?: number;
  /** When 1, the response gains a `counts` aggregate for the KPI header. */
  include_counts?: 1;
  /** 1-based page number (page_size 25, capped at 100 server-side). */
  page?: number;
}

/**
 * Search tenant users across ALL tenants for the superadmin global user
 * management page (TASK-3685). Backed by `GET /api/v1/admin/users/`
 * (AdminUserSearchView, superadmin-only). Returns the paginated envelope
 * ({count, next, previous, results}); each row carries its `tenant_id` /
 * `tenant_name` attribution. Mutations reuse {@link updateAdminUser} and
 * {@link resetUserPassword}.
 */
export async function searchAdminUsers(
  params: SearchAdminUsersParams = {},
): Promise<AdminGlobalUserPage> {
  const response = await apiClient.get<AdminGlobalUserPage>(
    "/v1/admin/users/",
    {
      params,
    },
  );
  return response.data;
}

/**
 * Fetch a tenant's plan, quota and feature flags together with current usage
 * vs limits (TASK-3924/3925). Backed by `GET /api/v1/admin/tenants/{id}/plan/`,
 * permission `IsSuperAdminOrDealer` — dealer-scoped server-side via
 * `tenants_visible_to`, so an out-of-scope tenant id returns 404. The plan is
 * auto-provisioned on first access; the four `max_*` quotas are `null` when
 * unlimited and the `usage` block reports current-vs-limit per dimension.
 */
export async function getTenantPlan(tenantId: number): Promise<TenantPlan> {
  const response = await apiClient.get<TenantPlan>(
    `/v1/admin/tenants/${tenantId}/plan/`,
  );
  return response.data;
}

/**
 * Partially update a tenant's plan name, quota limits and feature flags
 * (TASK-3924/3925). Backed by `PATCH /api/v1/admin/tenants/{id}/plan/`. A quota
 * sent as `null` clears the limit (unlimited); a negative value is rejected with
 * a 400 field error. `features.parcels_enabled` is the only settable flag —
 * unknown flag keys yield a 400. Returns the updated plan (including refreshed
 * `usage`).
 */
export async function updateTenantPlan(
  tenantId: number,
  data: UpdateTenantPlanData,
): Promise<TenantPlan> {
  const response = await apiClient.patch<TenantPlan>(
    `/v1/admin/tenants/${tenantId}/plan/`,
    data,
  );
  return response.data;
}

/**
 * Fetch general settings for a tenant (auto-created on first access).
 */
export async function getTenantSettings(
  tenantId: number,
): Promise<TenantSettings> {
  const response = await apiClient.get<TenantSettings>(
    `/v1/admin/tenants/${tenantId}/settings/`,
  );
  return response.data;
}

/**
 * Partially update general settings for a tenant.
 */
export async function updateTenantSettings(
  tenantId: number,
  data: Partial<TenantSettings>,
): Promise<TenantSettings> {
  const response = await apiClient.patch<TenantSettings>(
    `/v1/admin/tenants/${tenantId}/settings/`,
    data,
  );
  return response.data;
}

/**
 * Fetch planning settings for a tenant.
 */
export async function getTenantPlanningSettings(
  tenantId: number,
): Promise<TenantPlanningSettings> {
  const response = await apiClient.get<TenantPlanningSettings>(
    `/v1/admin/tenants/${tenantId}/planning-settings/`,
  );
  return response.data;
}

/**
 * Partially update planning settings for a tenant.
 */
export async function updateTenantPlanningSettings(
  tenantId: number,
  data: Partial<TenantPlanningSettings>,
): Promise<TenantPlanningSettings> {
  const response = await apiClient.patch<TenantPlanningSettings>(
    `/v1/admin/tenants/${tenantId}/planning-settings/`,
    data,
  );
  return response.data;
}

/**
 * Set a new password for a tenant user.
 */
export async function resetUserPassword(
  userId: number,
  password: string,
): Promise<{ detail: string }> {
  const response = await apiClient.post<{ detail: string }>(
    `/v1/admin/users/${userId}/set-password/`,
    { password },
  );
  return response.data;
}

/**
 * Send (or resend) an invitation email to a tenant user.
 */
export async function sendUserInvite(
  userId: number,
): Promise<{ detail: string; expires_at: string }> {
  const response = await apiClient.post<{ detail: string; expires_at: string }>(
    `/v1/admin/users/${userId}/invite/`,
  );
  return response.data;
}

/** Query parameters accepted by the unified activity-log feed (TASK-514). */
export interface ListActivityLogParams {
  /**
   * Primary actor category (TASK-520): "all" | "user" | "system" |
   * "impersonation". When set it supersedes `type` on the backend.
   * - `user`          — things people did via the web: audit rows with an
   *                     actor PLUS impersonation events (excludes system rows).
   * - `system`        — audit rows with no actor (background/historical).
   * - `impersonation` — impersonation events only.
   * - `all`           — everything (audit + impersonation), merge-sorted.
   */
  category?: ActivityLogCategory;
  /** Legacy item type filter: "all" | "audit" | "impersonation". */
  type?: ActivityLogType | "all";
  /** Actor user id (matches audit user or impersonation superadmin). */
  actor?: number;
  /** Audit target model label (excludes impersonation rows). */
  model?: string;
  /** Audit action create/update/delete (excludes impersonation rows). */
  action?: string;
  tenant_id?: number;
  date_from?: string;
  date_to?: string;
  page?: number;
}

/**
 * List the unified activity-log feed (audit + impersonation), type-tagged and
 * chronologically ordered (newest first). Backed by
 * `GET /api/v1/admin/activity-log/` (TASK-513). Returns the paginated envelope
 * ({count, next, previous, results}).
 */
export async function listActivityLog(
  params: ListActivityLogParams = {},
): Promise<ActivityLogPage> {
  const response = await apiClient.get<ActivityLogPage>(
    "/v1/admin/activity-log/",
    { params },
  );
  return response.data;
}

/**
 * Export the activity-log feed as a CSV, honoring the same filters as
 * `listActivityLog` (TASK-3612). Backed by
 * `GET /api/v1/admin/activity-log/export/` (TASK-3611).
 *
 * Returns the CSV blob together with the filename suggested by the response
 * `Content-Disposition` header (falls back to a sensible default when absent).
 */
export async function exportActivityLog(
  params: ListActivityLogParams = {},
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get<Blob>("/v1/admin/activity-log/export/", {
    params,
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"];
  const filename =
    parseContentDispositionFilename(
      typeof disposition === "string" ? disposition : undefined,
    ) ?? "activity-log.csv";
  return { blob: response.data, filename };
}

/**
 * Extract the `filename` from a `Content-Disposition` header value.
 * Supports both `filename="..."` and the RFC 5987 `filename*=UTF-8''...` form.
 */
function parseContentDispositionFilename(
  disposition: string | undefined,
): string | null {
  if (!disposition) {
    return null;
  }
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // Fall through to the plain `filename=` form.
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  return plainMatch?.[1] ?? null;
}

/**
 * Fetch billing summary (current month + 3-month history) for a tenant.
 */
export async function getTenantBilling(
  tenantId: number,
): Promise<TenantBilling> {
  const response = await apiClient.get<TenantBilling>(
    `/v1/admin/tenants/${tenantId}/billing/`,
  );
  return response.data;
}

/**
 * Fetch global platform settings (billing_currency, billing_rate).
 */
export async function getPlatformSettings(): Promise<PlatformSettings> {
  const response = await apiClient.get<PlatformSettings>(
    "/v1/admin/platform-settings/",
  );
  return response.data;
}

/**
 * Partially update global platform settings.
 */
export async function updatePlatformSettings(
  data: Partial<PlatformSettings>,
): Promise<PlatformSettings> {
  const response = await apiClient.patch<PlatformSettings>(
    "/v1/admin/platform-settings/",
    data,
  );
  return response.data;
}

/**
 * Fetch billing settings for a specific tenant.
 * Creates default settings record if none exists.
 */
export async function getTenantBillingSettings(
  tenantId: number,
): Promise<TenantBillingSettings> {
  const response = await apiClient.get<TenantBillingSettings>(
    `/v1/admin/tenants/${tenantId}/billing-settings/`,
  );
  return response.data;
}

/**
 * Partially update billing settings for a specific tenant.
 */
export async function updateTenantBillingSettings(
  tenantId: number,
  data: Partial<TenantBillingSettingsFormData>,
): Promise<TenantBillingSettings> {
  const response = await apiClient.patch<TenantBillingSettings>(
    `/v1/admin/tenants/${tenantId}/billing-settings/`,
    data,
  );
  return response.data;
}

/**
 * Generate monthly billing records for all active tenants (or specific ones).
 */
export async function generateMonthlyBilling(
  year: number,
  month: number,
  tenantIds?: number[],
): Promise<GenerateBillingResponse> {
  const body: { year: number; month: number; tenant_ids?: number[] } = {
    year,
    month,
  };
  if (tenantIds !== undefined) {
    body.tenant_ids = tenantIds;
  }
  const response = await apiClient.post<GenerateBillingResponse>(
    "/v1/admin/billing/generate/",
    body,
  );
  return response.data;
}

/**
 * List all billing records with optional filters.
 *
 * `ordering` (TASK-3654) whitelists `period_start` (default), `total_amount` and
 * `status`; prefix with `-` for descending (e.g. `-total_amount`).
 */
export async function listAdminBillingRecords(params: {
  year?: number;
  month?: number;
  tenant_id?: number;
  status?: string;
  ordering?: string;
  /** 1-based page number (TASK-3703). Backend default page_size is 20. */
  page?: number;
  /** Per-page override, capped at 100 server-side (TASK-3703). */
  page_size?: number;
}): Promise<BillingRecordListResponse> {
  const response = await apiClient.get<BillingRecordListResponse>(
    "/v1/admin/billing/records/",
    { params },
  );
  return response.data;
}

/**
 * Fetch ALL billing records for a given year by paging the existing list
 * endpoint (TASK-3889). Used to derive the client-side monthly revenue trend
 * and billed-vs-paid roll-up without a new aggregate endpoint. Dealer-scoped
 * server-side via `tenants_visible_to`. Pages at `page_size: 100` and stops when
 * a short page is returned, `next` is null, or a defensive 10-page cap is hit.
 */
export async function listAllAdminBillingRecordsForYear(
  year: number,
): Promise<BillingRecord[]> {
  const PAGE_SIZE = 100;
  const MAX_PAGES = 10;
  const all: BillingRecord[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const data = await listAdminBillingRecords({
      year,
      page,
      page_size: PAGE_SIZE,
    });
    all.push(...data.results);
    if (data.results.length < PAGE_SIZE || !data.next) {
      break;
    }
  }

  return all;
}

/**
 * Partially update a billing record (status and/or notes).
 */
export async function updateBillingRecord(
  id: number,
  data: { status?: string; notes?: string },
): Promise<BillingRecord> {
  const response = await apiClient.patch<BillingRecord>(
    `/v1/admin/billing/records/${id}/`,
    data,
  );
  return response.data;
}

/**
 * Recalculate a DRAFT billing record against the current BillingStopEvents
 * (TASK-3650). Returns the updated record. The backend responds 409 if the
 * record is final (ISSUED/PAID/VOID) — callers should surface that message.
 */
export async function recalculateBillingRecord(
  id: number,
): Promise<BillingRecord> {
  const response = await apiClient.post<BillingRecord>(
    `/v1/admin/billing/records/${id}/recalculate/`,
    {},
  );
  return response.data;
}

/** Query parameters accepted by the invoice-breakdown endpoint (TASK-3652). */
export interface GetBillingRecordBreakdownParams {
  /** 1-based page number (page_size 20, server-side). */
  page?: number;
}

/**
 * Fetch the BillingStopEvents that make up a billing record (TASK-3652) so the
 * invoice total is auditable. Backed by
 * `GET /api/v1/admin/billing/records/{id}/breakdown/` (IsSuperAdminOrDealer,
 * dealer-scoped — out-of-scope / unknown record id → 404). Returns the
 * paginated events plus an auditing `summary` (order vs depot counts, rate,
 * total). For a DRAFT record `summary.total_count` equals the record's
 * `stop_count`.
 */
export async function getBillingRecordBreakdown(
  recordId: number,
  params: GetBillingRecordBreakdownParams = {},
): Promise<BillingRecordBreakdown> {
  const response = await apiClient.get<BillingRecordBreakdown>(
    `/v1/admin/billing/records/${recordId}/breakdown/`,
    { params },
  );
  return response.data;
}

/**
 * Record a payment against a billing record (TASK-3928/3929), transitioning it
 * to PAID. Backed by `POST /api/v1/admin/billing/records/{id}/record-payment/`
 * (IsSuperAdminOrDealer, dealer-scoped — out-of-scope / unknown id → 404).
 * Lifecycle errors (VOID / already-PAID record, missing `paid_date`) surface as
 * `400 {"detail": ...}` / field errors which the caller maps onto the form.
 */
export async function recordBillingPayment(
  id: number,
  data: RecordPaymentData,
): Promise<BillingRecord> {
  const response = await apiClient.post<BillingRecord>(
    `/v1/admin/billing/records/${id}/record-payment/`,
    data,
  );
  return response.data;
}

/**
 * List credit notes for a billing record (TASK-3928/3929). Backed by
 * `GET /api/v1/admin/billing/records/{id}/credit-notes/` (IsSuperAdminOrDealer,
 * dealer-scoped — out-of-scope / unknown id → 404). Paginated.
 */
export async function listCreditNotes(
  recordId: number,
  params: { page?: number; page_size?: number } = {},
): Promise<CreditNoteListResponse> {
  const response = await apiClient.get<CreditNoteListResponse>(
    `/v1/admin/billing/records/${recordId}/credit-notes/`,
    { params },
  );
  return response.data;
}

/**
 * Create a credit note against a billing record (TASK-3928/3929). Backed by
 * `POST /api/v1/admin/billing/records/{id}/credit-notes/`. The created note
 * inherits the record's tenant + currency server-side. Invalid input → `400`
 * field errors which the caller maps onto the form.
 */
export async function createCreditNote(
  recordId: number,
  data: CreateCreditNoteData,
): Promise<CreditNote> {
  const response = await apiClient.post<CreditNote>(
    `/v1/admin/billing/records/${recordId}/credit-notes/`,
    data,
  );
  return response.data;
}

/**
 * Fetch superadmin billing KPI stats for a given year/month.
 */
export async function getAdminBillingStats(
  year: number,
  month: number,
): Promise<AdminBillingStats> {
  const response = await apiClient.get<AdminBillingStats>(
    "/v1/admin/billing/stats/",
    { params: { year, month } },
  );
  return response.data;
}

/**
 * Export billing records as a CSV blob for the given filters.
 */
export async function exportAdminBillingRecords(params: {
  year?: number;
  month?: number;
  status?: string;
  tenant_id?: number;
}): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    "/v1/admin/billing/records/export/",
    { params, responseType: "blob" },
  );
  return response.data;
}

/** Query parameters accepted by the superadmin billing stop-events list. */
export interface ListAdminBillingStopEventsParams {
  tenant_id?: number;
  /** Filter on `event_at` date (YYYY-MM-DD), inclusive. */
  date_from?: string;
  /** Filter on `event_at` date (YYYY-MM-DD), inclusive. */
  date_to?: string;
  stop_type?: AdminBillingStopEventType;
  /** Filter by delivery-attempt counter. */
  attempt?: number;
  page?: number;
  /** Rows per page (backend default 20, capped server-side at 100; TASK-3680). */
  page_size?: number;
}

/**
 * List billable stop events (the per-event ledger behind the invoice counts)
 * for the billing audit view (TASK-3636). Backed by
 * `GET /api/v1/admin/billing/stop-events/` (TASK-3635), permission
 * `IsSuperAdminOrDealer` (dealer-scoped — a dealer sees only their own
 * portfolio, a superadmin sees platform-wide), page_size 20. Returns the
 * paginated envelope ({count, next, previous, results}); `count` is the total
 * deduped billable-point count for the filters.
 */
export async function listAdminBillingStopEvents(
  params: ListAdminBillingStopEventsParams = {},
): Promise<AdminBillingStopEventListResponse> {
  const response = await apiClient.get<AdminBillingStopEventListResponse>(
    "/v1/admin/billing/stop-events/",
    { params },
  );
  return response.data;
}

/**
 * Export the billable stop-events ledger as a CSV (TASK-3654/3661), honoring the
 * SAME filters as `listAdminBillingStopEvents` (tenant / stop-type / date range /
 * attempt) and the same dealer scoping. Backed by
 * `GET /api/v1/admin/billing/stop-events/export/` (IsSuperAdminOrDealer). Returns
 * the CSV blob together with the filename suggested by the response
 * `Content-Disposition` header (falls back to `billing-stop-events.csv`).
 */
export async function exportStopEventsCsv(
  params: Omit<ListAdminBillingStopEventsParams, "page"> = {},
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get<Blob>(
    "/v1/admin/billing/stop-events/export/",
    { params, responseType: "blob" },
  );
  const disposition = response.headers["content-disposition"];
  const filename =
    parseContentDispositionFilename(
      typeof disposition === "string" ? disposition : undefined,
    ) ?? "billing-stop-events.csv";
  return { blob: response.data, filename };
}

/**
 * Bulk-generate (and optionally issue) monthly billing for an explicit set of
 * tenant ids (TASK-3654/3661), dealer-scoped. Out-of-scope ids are skipped with
 * reason `out_of_scope`; zero-event tenants with `no_billable_stops`; an existing
 * final record with `record_exists`. When `issue=true`, generated non-zero DRAFT
 * records are issued. Backed by `POST /api/v1/admin/billing/bulk-generate/`.
 */
export async function bulkGenerateBilling(data: {
  tenant_ids: number[];
  year: number;
  month: number;
  issue?: boolean;
}): Promise<BulkGenerateBillingResponse> {
  const response = await apiClient.post<BulkGenerateBillingResponse>(
    "/v1/admin/billing/bulk-generate/",
    data,
  );
  return response.data;
}

/**
 * Fetch the historical monthly MRR (revenue) series (TASK-3897/3898). Backed by
 * `GET /api/v1/admin/billing/mrr-series/`, `IsSuperAdminOrDealer` — dealer-scoped
 * server-side via `tenants_visible_to` (a dealer sees only their portfolio's
 * revenue, a superadmin the platform-wide series). The `series` is ascending
 * (oldest → newest) and zero-filled; `months` defaults to 12 on the backend.
 */
export async function getAdminMrrSeries(
  months?: number,
  range: AdminAnalyticsRangeParams = {},
): Promise<MrrSeriesResponse> {
  const params: Record<string, string | number> = {};
  if (months !== undefined) {
    params.months = months;
  }
  // Range-aware (TASK-4313): a custom range scopes the series to explicit dates.
  if (range.date_from && range.date_to) {
    params.date_from = range.date_from;
    params.date_to = range.date_to;
  }
  const response = await apiClient.get<MrrSeriesResponse>(
    "/v1/admin/billing/mrr-series/",
    Object.keys(params).length > 0 ? { params } : undefined,
  );
  return response.data;
}

/**
 * Fetch the churn-risk-scored tenant list (TASK-3901/3902). Backed by
 * `GET /api/v1/admin/analytics/churn-risk/`, `IsSuperAdminOrDealer` —
 * dealer-scoped server-side. `results` is already sorted by score descending;
 * each row carries the level band plus the underlying signals (days since last
 * billable event, month-over-month activity drop).
 */
export async function getAdminChurnRisk(): Promise<ChurnRiskResponse> {
  const response = await apiClient.get<ChurnRiskResponse>(
    "/v1/admin/analytics/churn-risk/",
  );
  return response.data;
}

/**
 * Fetch the signup-cohort monthly-active retention grid (TASK-3899/3900). Backed
 * by `GET /api/v1/admin/analytics/retention/`, `IsSuperAdminOrDealer` —
 * dealer-scoped server-side. `cohorts` is ascending by signup month; each cohort
 * carries its size and a per-offset retention curve (offset 0 = signup month).
 * `months` defaults to 12 on the backend.
 */
export async function getAdminRetention(
  months?: number,
  range: AdminAnalyticsRangeParams = {},
): Promise<RetentionResponse> {
  const params: Record<string, string | number> = {};
  if (months !== undefined) {
    params.months = months;
  }
  // Range-aware (TASK-4313): a custom range scopes the cohort grid to dates.
  if (range.date_from && range.date_to) {
    params.date_from = range.date_from;
    params.date_to = range.date_to;
  }
  const response = await apiClient.get<RetentionResponse>(
    "/v1/admin/analytics/retention/",
    Object.keys(params).length > 0 ? { params } : undefined,
  );
  return response.data;
}

/**
 * Fetch unit economics — LTV, CAC and the LTV:CAC ratio (TASK-3905/3906). Backed
 * by `GET /api/v1/admin/analytics/unit-economics/`, `IsSuperAdminOrDealer` —
 * dealer-scoped server-side via `tenants_visible_to` (a dealer's LTV spans only
 * their portfolio). CAC is the global `PlatformSettings.acquisition_cost`; when
 * it is unset/0 both `cac` and `ltv_cac_ratio` are `null` (the UI must render a
 * dash — no divide-by-zero, Sprint 107 lesson).
 */
export async function getAdminUnitEconomics(): Promise<UnitEconomicsResponse> {
  const response = await apiClient.get<UnitEconomicsResponse>(
    "/v1/admin/analytics/unit-economics/",
  );
  return response.data;
}

/**
 * Compose and send an admin→tenant broadcast (TASK-3930/3931). SUPERADMIN ONLY
 * — dealers / company_admins / dispatchers / drivers receive 403. Backed by
 * `POST /api/v1/admin/broadcasts/`; on send the backend fans out tenant-wide
 * IN_APP notification rows (delivered via the existing bell). When
 * `audience === "subset"` the `tenants` ids must be supplied. Invalid input →
 * `400` field errors which the caller maps onto the form.
 */
export async function sendBroadcast(
  data: CreateBroadcastData,
): Promise<Broadcast> {
  const response = await apiClient.post<Broadcast>(
    "/v1/admin/broadcasts/",
    data,
  );
  return response.data;
}

/**
 * The resolved audience size of a saved broadcast (TASK-4312). The backend
 * computes `audience_count` on the `BroadcastSerializer` — it resolves the
 * tenant count for `subset` AND the `plan` / `dealer` / `jurisdiction` segments.
 * There is NO pre-send preview endpoint, so this reads the field off a saved
 * broadcast (e.g. the just-sent response). Isolated behind one function so it is
 * swappable if a dedicated dry-run/preview endpoint is added later (decision d).
 */
export function broadcastAudienceCount(
  broadcast: Broadcast,
): number | undefined {
  return broadcast.audience_count;
}

/**
 * Global admin search across tenants / users / dealers / invoices (Sprint 142,
 * TASK-4220). Backed by `GET /api/v1/admin/search/?q=<term>`, permission
 * `IsSuperAdminOrDealer` — role-scoped server-side: a superadmin sees all, a
 * dealer sees only their `tenants_visible_to` tenants/users/invoices plus an
 * empty `dealers` group. Each item carries a `type` + an absolute admin `url`
 * the command palette rewrites to the active shell base path before navigating.
 */
export async function adminSearch(q: string): Promise<AdminSearchResponse> {
  const response = await apiClient.get<AdminSearchResponse>(
    "/v1/admin/search/",
    { params: { q } },
  );
  return response.data;
}

/**
 * List active and past broadcasts (Sprint 142, TASK-4224/4226). Backed by
 * `GET /api/v1/admin/broadcasts/`, SUPERADMIN ONLY. Returns the paginated
 * envelope ({count, next, previous, results}); each row carries a server-derived
 * `status` lifecycle label. The composer's active/past list consumes this; it is
 * built to degrade gracefully if the endpoint is not yet deployed.
 */
export async function listBroadcasts(
  params: { page?: number } = {},
): Promise<BroadcastListResponse> {
  const response = await apiClient.get<BroadcastListResponse>(
    "/v1/admin/broadcasts/",
    { params },
  );
  return response.data;
}

/**
 * Fetch the current platform maintenance banner (TASK-3930). SUPERADMIN ONLY.
 * Backed by `GET /api/v1/admin/maintenance-banner/`.
 */
export async function getMaintenanceBanner(): Promise<MaintenanceBanner> {
  const response = await apiClient.get<MaintenanceBanner>(
    "/v1/admin/maintenance-banner/",
  );
  return response.data;
}

/**
 * Set / clear the platform maintenance banner (TASK-3930). SUPERADMIN ONLY.
 * Backed by `PATCH /api/v1/admin/maintenance-banner/`. Returns the updated
 * record.
 */
export async function setMaintenanceBanner(
  data: UpdateMaintenanceBannerData,
): Promise<MaintenanceBanner> {
  const response = await apiClient.patch<MaintenanceBanner>(
    "/v1/admin/maintenance-banner/",
    data,
  );
  return response.data;
}

// Re-export types that callers may need alongside the functions.
export type {
  AdminSearchItem,
  AdminSearchResponse,
  AdminSearchType,
  Broadcast,
  BroadcastAudience,
  BroadcastCountry,
  BroadcastListItem,
  BroadcastListResponse,
  BroadcastPlanTier,
  CreateBroadcastData,
  MaintenanceBanner,
  UpdateMaintenanceBannerData,
  ActivityLogItem,
  ActivityLogPage,
  AdminAlert,
  AdminAlertList,
  AdminAlertMarkResponse,
  AdminBillingStats,
  AdminBillingStopEvent,
  AdminBillingStopEventListResponse,
  AdminBillingStopEventType,
  AdminDealer,
  AdminDealerListResponse,
  AdminTenantDetail,
  AdminTenantPage,
  AdminTenantUserPage,
  BillingMonth,
  BillingRecord,
  BillingRecordBreakdown,
  BillingRecordListResponse,
  BulkGenerateBillingResponse,
  BulkTenantUpdateResponse,
  ChurnRiskResponse,
  ChurnRiskRow,
  GenerateBillingResponse,
  MrrSeriesPoint,
  MrrSeriesResponse,
  RetentionCohort,
  RetentionResponse,
  PlatformSettings,
  TenantBilling,
  TenantBillingSettings,
  TenantPlan,
  TenantPlanningSettings,
  TenantSettings,
  UnitEconomicsResponse,
};
